import './sync/df-ajax';
import { markRaw } from 'vue';
import { getActivePinia } from 'pinia';
import { useDataStore } from './stores/data';
import { sendMessage } from './message-sender';

const TLD_LIST = [ 'com', 'net', 'org', 'dev', 'diet' ];

// Returns the actual name of the function on the window object
function addFunctionToWindow(component, func, baseName) {
    for (let i = 0; true; i++) {
        let name = `${baseName}${i}`;
        if (!window.hasOwnProperty(name)) {
            window[name] = func.bind(component);
            return name;
        }
    }
}

// Takes the returned name from addFunctionToWindow
function removeFunctionFromWindow(name) {
    if (name) window[name] = null;
}

function alignPopovers(component) {
    Object.entries(component.$refs).forEach(kv => {
        var ref = kv[1];

        if (ref && ref.alignOverlay && ref.visible && ref.$name == 'Popover') {
            ref.alignOverlay();
        }
    });
}

// Creates a new "animation bag" - an object that holds animations for
// later cancellation until they are cancelled or finished.
// For convenience, the add function accepts a function that can be
// automatically called when the animation is finished (but not cancelled).
// When cancel is called, all animations are cancelled and removed.
// The object is automatically passed through Vue's markRaw function.
// Do not rely on its inner state looking any particular way.
function animationBag() {
    var bag = markRaw({
        animations: [],
    });

    // Adds an animation until it is cancelled or finished
    bag.add = function(animation, onFinished) {
        cleanup();
        if (onFinished) {
            animation.addEventListener('finish', onFinished);
        }
        animation.addEventListener('finish', cleanup);
    };

    // Cancels all animations
    bag.cancel = function() {
        bag.animations.forEach(a => a.cancel());
        cleanup();
    };

    bag.show = function(el, duration, onFinished) {
        showHide(true, el, duration, onFinished);
    };

    bag.hide = function(el, duration, onFinished) {
        showHide(false, el, duration, onFinished);
    };

    function showHide(showFlag, el, duration, onFinished) {
        if (getComputedStyle(el).getPropertyValue('display') == 'none') {
            el.style.display = 'block';
        }

        duration = duration || 300;

        var height = el.getBoundingClientRect().height;
        var animation = null;
        if (showFlag) {
            animation = el.animate([
                {
                    height: '0px',
                    opacity: '0',
                },
                {
                    height: `${height}px`,
                    opacity: '1',
                },
            ], duration);
        } else {
            animation = el.animate([
                {
                    height: `${height}px`,
                    opacity: '1',
                },
                {
                    height: '0px',
                    opacity: '0',
                },
            ], duration);
        }
        animation.addEventListener('finish', function() {
            el.style.removeProperty('display');
        });
        bag.add(animation, onFinished);
    }

    // Removes all animations that are either "idle" (meaning cancelled) or "finished"
    function cleanup() {
        for (let i = bag.animations.length - 1; i >= 0; i--) {
            switch (bag.animations[i].readyState) {
                case 'idle':
                case 'finished':
                    bag.animations.splice(i, 1);
                    break;
            }
        }
    }

    return bag;
}

// Add as a computed option like: cancelProps: computedCancelProps,
// Requires that the "data" store be added as a computed option like: ...mapStores(useDataStore),
// Use on a cancel button like: <Button label="Cancel" v-bind="cancelProps"></Button>
function computedCancelProps() {
    if (this.dataStore.admin) {
        return { variant: 'text' };
    } else {
        return { variant: 'outlined' };
    }
}

// Call this when the component is mounted
// Returns a cleanup function to be called when it is unmounted
function closeDialogOnEscape(component) {
    var eventHandler = listenForEscape.bind(component);
    addEventListener('keydown', eventHandler);

    return function cleanup() {
        removeEventListener('keydown', eventHandler);
    };

    function listenForEscape(e) {
        if (e.key == 'Escape') {
            this.closeDialog();
        }
    }
}

// dateStyle can be: "full", "long", "medium", or "short"
// See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#datestyle
function formatDate(date, dateStyle) {
    if (date) {
        // According to MDN: Support for ISO 8601 formats differs in that date-only strings (e.g. "1970-01-01") are treated as UTC, not local.
        // So when converting to a string, we want UTC because local time might be a different day.
        // Undefined means use whatever the default is - can also force 'en-US'
        return new Date(date).toLocaleDateString(undefined, { timeZone: 'UTC', dateStyle: dateStyle || 'medium' });
    } else {
        return '';
    }
}

function formatPrice(price) {
    if (price == null) {
        return null;
    }
    price = Number(price);
    if (Number.isNaN(price)) {
        return null;
    }

    return price.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: 'narrowSymbol',
    });
}

function formatTimestamp(timestamp) {
    if (timestamp) {
        // Timestamps specify the UTC time zone.
        return new Date(timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short',
        });
    } else {
        return '';
    }
}

// Add this to the methods of a component, and then put on child components
// that might emit it: @update-data="forwardUpdateData"
function forwardUpdateData(data) {
    // Does nothing, now that we use Pinia.
    //this.$emit('updateData', data);
}

// Narrow replacement for tldts getDomain({ extractHostname: false, detectIp: false, validHosts: ['localhost'] })
function getDomain(hostname) {
    if (!hostname) return null;

    hostname = String(hostname).trim().toLowerCase();
    if (hostname.endsWith('.')) {
        hostname = hostname.slice(0, -1);
    }
    if (!hostname) return null;

    if (hostname === 'localhost') return 'localhost';

    const tlds = TLD_LIST.slice().sort((a, b) => b.split('.').length - a.split('.').length);
    for (const tld of tlds) {
        if (hostname === tld) {
            return null;
        }
        const suffix = '.' + tld;
        if (!hostname.endsWith(suffix)) continue;

        const remainder = hostname.slice(0, -suffix.length);
        if (!remainder) return null;

        const lastDot = remainder.lastIndexOf('.');
        const label = lastDot === -1 ? remainder : remainder.slice(lastDot + 1);
        if (!label) return null;

        return label + suffix;
    }

    return null;
}

// Expects:
//   component: can also be "this" - the component with the dialog or prop
//   name: Then name of the value to get
//   canBeFalsy: If falsy values might be valid, set this to a truthy value
//   defaultValue: If set to a function, it is called for the default.
//                 If set to null or undefined, null is the default.
//                 Otherwise it acts as the default.
function getFromPropOrDialog(opt) {
    const component = this || opt.component;
    const name = opt.name;

    let value = component[name];
    if (isSet(value)) {
        return value;
    }

    const dialogData = component.dialogRef?.data;
    if (dialogData) {
        value = dialogData[name];
        if (isSet(value)) return value;
    }

    if (opt.defaultValue == null) {
        return null;
    } else if (typeof opt.defaultValue === 'function') {
        return opt.defaultValue();
    } else {
        return opt.defaultValue;
    }

    function isSet(value) {
        if (opt.canBeFalsy) {
            return value != null;
        } else {
            return !!value;
        }
    }
}

function htmlEncode(text) {
    var div = document.createElement('div');
    div.innerText = text;
    return div.innerHTML;
}

function includeStylesheet(opt) {
    var link = document.createElement('link');
    opt = Object.assign({ rel: 'stylesheet', type: 'text/css' }, opt);
    for (const [key, value] of Object.entries(opt)) {
        switch (key) {
            case 'href':
            case 'rel':
            case 'type':
                link[key] = value;
                break;
            default:
                link.setAttribute(key, value);
                break;
        }
    }
    document.head.appendChild(link);
}

function isSet(value) {
    if (!value) {
        return false;
    }

    value = String(value).trim();
    return value.length > 0;
}

function loadScript(opt) {
    var script = document.createElement('script');
    for (const [key, value] of Object.entries(opt)) {
        switch (key) {
            case 'src':
            case 'async':
            case 'onload':
                script[key] = value;
                break;
            default:
                script.setAttribute(key, value);
                break;
        }
    }
    document.body.appendChild(script);
}

function markupCompoundPrice(price) {
    if (price.salePrice) {
        return `<s class="old-price">${htmlEncode(formatPrice(price.price))}</s> <span class="price">${htmlEncode(formatPrice(price.salePrice))}</span>`;
    } else {
        return `<span class="price">${htmlEncode(formatPrice(price.price))}</span>`;
    }
}

function multiplyCompoundPrice(price, qty) {
    if (!price) {
        return null;
    }

    var result = {
        price: price.price * qty,
    };
    if (price.salePrice) {
        result.salePrice = price.salePrice * qty;
        result.currentPrice = result.salePrice;
    } else {
        result.salePrice = null;
        result.currentPrice = result.price;
    }
    return result;
}

// If the response has a dataid, then the data is automatically added to
// the "data" store and the 'update-data' message is sent to the parent
// window.
// Accepts the following options:
//  component: Ignored, now that we use Pinia.
//  store: The Pinia "data" store.
//  action: The action name to send to the endpoint
//  payload: The payload object to send back to the server.
//  success: A function to be called when successful - the only argument is the result object from the dfajax call
//  failure: A function to be called when errored - the only argument is the result object from the dfajax call
//  completed: Called at the end, regardless of success or failure.
function sendUpdate(opt) {
    var store = opt.store || useDataStore(getActivePinia());

    var payload = opt.payload;
    if (store.admin) {
        payload = Object.assign({}, payload, {
            admin: true,
            accountId: store.accountId,
        });
    }

    dfajax({
        handler: 'manage-minibc-subscriptions',
        action: opt.action,
        crossOrigin: 'auto',
        method: 'POST',
        payload: payload,
        successCallback: function(data, result) {
            if (data.dataid) {
                store.updateData(data);

                sendMessage({
                    DRF: {
                        subject: 'update-data',
                        dataid: data.dataid,
                    },
                });
            }

            if (opt.success) opt.success(result);
        },
        errorCallback: function(result) {
            if (opt.failure) opt.failure(result);
        },
        finallyCallback: function() {
            if (opt.completed) opt.completed();
        },
    });
}

function setCssVariables(scope, values) {
    for (const [key, value] of Object.entries(values)) {
        if (value == null) {
            // for now, ignore null or undefined values
            // note setProperty is documented to treat these as empty string
        } else if (typeof value === 'object') {
            setCssVariables(`${scope}-${key}`, value);
        } else {
            document.documentElement.style.setProperty(`--${scope}-${key}`, value);
        }
    }
}

// Expects:
//   any valid input for triggerDialog (mode, width, id)
//   prime: An object containing options only useful for PrimeVue dialogs (not used in production)
// Prime is ignored, now that Prime View dialogs aren't used, but it used to have:
//   popover: The popover ref to open in place of a dialog
//   event: Event object for the popover
//   parent: The component requesting the dialog (can also be passed as this)
//   child: The component to use inside the dialog when loaded directly
//   header: Header string for the dialog
//   data: Data object for the dialog
function showDialog(opt) {
    var data = useDataStore(getActivePinia());
    if (data.supportsDialog) {
        opt = Object.assign({}, opt);
        delete opt.prime;

        triggerDialog(opt);
    } else {
        // This shouldn't happen in production, during proper embed scenarios.
        alert('Failed to show the dialog');
    }
}

// Expects:
//   messageName: Name of the message
//   parent: Ignored, now that Prime View dialogs aren't used - The component displaying the message
//   child: Ignored, now that Prime View dialogs aren't used - The actual ErrorMessage component, for use in a PrimeVue dialog if necessary
//   data: Optional object with props to pass to the ErrorMessage component
function showErrorDialog(opt) {
    var data = opt.data || {};
    showDialog({
        mode: 'error-message',
        name: opt.messageName,
        ...data,
        width: 450,
        prime: {
            parent: opt.parent,
            child: opt.child,
            header: 'Error',
            data,
        },
    });
}

const supportsSessionStorage = (function () {
    try {
        var t = '__storage_test__';
        var storage = window.sessionStorage;
        storage.setItem(t, t);
        storage.removeItem(t);
        return true;
    } catch {
        return false;
    }
})();

function triggerDialog(opt) {
    var data = useDataStore(getActivePinia());

    var adminInfo = { };
    if (data.admin) {
        adminInfo.admin = true;
        adminInfo.accountId = data.accountId;
    }

    sendMessage({
        DRF: {
            subject: 'show-dialog',
            dataid: data.dataid,
            ...adminInfo,
            ...opt,
        },
    });
}

export {
    addFunctionToWindow,
    removeFunctionFromWindow,
    alignPopovers,
    animationBag,
    computedCancelProps,
    closeDialogOnEscape,
    formatDate,
    formatPrice,
    formatTimestamp,
    forwardUpdateData,
    getDomain,
    getFromPropOrDialog,
    htmlEncode,
    includeStylesheet,
    isSet,
    loadScript,
    markupCompoundPrice,
    multiplyCompoundPrice,
    sendUpdate,
    setCssVariables,
    showDialog,
    showErrorDialog,
    supportsSessionStorage,
    triggerDialog,
};